<template>
  <div class="gs-checkout-flow-options">
    <section class="gs-checkout-flow-options__section">
      <div class="gs-checkout-flow-options__copy">
        <h3>Checkout flow</h3>
        <p>Choose how much information buyers see on each checkout screen.</p>
      </div>
      <div class="gs-checkout-flow-options__type-grid">
        <div v-for="checkoutStepType in checkoutStepTypes" :key="checkoutStepType.id">
        <gd-custom-radio
          v-model="selCheckoutStepType"
          :option="checkoutStepType"
          @onUpdateOption="changeStep"
        />
        </div>
      </div>
    </section>

    <section class="gs-checkout-flow-options__section">
      <div class="gs-checkout-flow-options__copy">
        <h3>{{ selCheckoutStepType === 0 || selCheckoutStepType === 2 ? 'Page sequence' : 'Checkout layout' }}</h3>
        <p>
          {{ selCheckoutStepType === 0 || selCheckoutStepType === 2
            ? 'Select the sequence that best matches the information you collect.'
            : 'Choose the buyer-facing layout for this checkout.' }}
        </p>
      </div>
      <div v-if="selCheckoutStepType === 0 || selCheckoutStepType === 2" class="gs-checkout-flow-options__layout-grid">
        <div v-for="multiStepType in multiStepTypes" :key="multiStepType.id">
        <checkout-radio-button
          v-model="selectedMultiStepType"
          :option="multiStepType"
          @onUpdateOption="multiStepTypeChange"
        />
        </div>
      </div>

      <div v-else class="gs-checkout-flow-options__layout-grid gs-checkout-flow-options__layout-grid--single">
        <div v-for="singleStepType in singleStepTypes" :key="singleStepType.id">
        <checkout-radio-button
          v-model="selectedSingleStepType"
          :option="singleStepType"
          @onUpdateOption="singleStepTypeChange"
        />
        </div>
      </div>
    </section>

    <section v-if="selCheckoutStepType === 0" class="gs-checkout-flow-options__section">
      <div class="gs-checkout-flow-options__switch-row">
        <div>
          <h3>{{ $t('forntend_checkout_link_advance_multistep_checkout') }}</h3>
          <p>Send buyers to your own pages between checkout steps. Leave this off for the hosted GrooveSell flow.</p>
        </div>
        <gd-toggle
          v-model="isMultiStepRedirect"
          :value="isMultiStepRedirect"
          :aria-label="$t('forntend_checkout_link_advance_multistep_checkout')"
          @change="multiStepRedirectChange"
        />
      </div>

      <div v-if="isMultiStepRedirect" class="gs-checkout-flow-options__redirects">
        <template v-if="selectedMultiStepType === 3">
          <div class="gs-checkout-flow-options__field">
            <label>{{ $t('forntend_checkout_link_advance_multistep_order_form_url') }}</label>
            <div>
              <text-input
                class="w-full"
                v-model="
                  localModel.multi_step_redirect_options.order_form_redirects
                    .order_form_url
                "
                :placeholder="
                  $t('forntend_checkout_link_advance_multistep_order_form_url')
                "
                :validator="
                  $v.localModel.multi_step_redirect_options.order_form_redirects
                    .order_form_url
                "
              />
            </div>
          </div>
        </template>
        <template v-else-if="selectedMultiStepType === 2">
          <div class="gs-checkout-flow-options__field">
            <label>{{ $t('forntend_checkout_link_advance_multistep_billing_form_url') }}</label>
            <div>
              <text-input
                class="w-full"
                v-model="
                  localModel.multi_step_redirect_options.email_form_redirects
                    .billing_form_url
                "
                :placeholder="
                  $t(
                    'forntend_checkout_link_advance_multistep_billing_form_url'
                  )
                "
                :validator="
                  $v.localModel.multi_step_redirect_options.email_form_redirects
                    .billing_form_url
                "
              />
            </div>
          </div>
          <div
            class="gs-checkout-flow-options__field"
            v-if="
              localModel.checkout_link_page_options.move_password_to_last_step
            "
          >
            <label>{{ $t('forntend_checkout_link_advance_multistep_password_form_url') }}</label>
            <div>
              <text-input
                class="w-full"
                v-model="
                  localModel.multi_step_redirect_options.email_form_redirects
                    .password_form_url
                "
                :placeholder="
                  $t(
                    'forntend_checkout_link_advance_multistep_password_form_url'
                  )
                "
                :validator="
                  $v.localModel.multi_step_redirect_options.email_form_redirects
                    .password_form_url
                "
              />
            </div>
          </div>
          <div class="gs-checkout-flow-options__field">
            <label>{{ $t('forntend_checkout_link_advance_multistep_payment_form_url') }}</label>
            <div>
              <text-input
                class="w-full"
                v-model="
                  localModel.multi_step_redirect_options.email_form_redirects
                    .payment_form_url
                "
                :placeholder="
                  $t(
                    'forntend_checkout_link_advance_multistep_payment_form_url'
                  )
                "
                :validator="
                  $v.localModel.multi_step_redirect_options.email_form_redirects
                    .payment_form_url
                "
              />
            </div>
          </div>
        </template>
        <template v-else-if="selectedMultiStepType === 1">
          <div class="gs-checkout-flow-options__field">
            <label>{{ $t('forntend_checkout_link_advance_multistep_billing_form_url') }}</label>
            <div>
              <text-input
                class="w-full"
                v-model="
                  localModel.multi_step_redirect_options.contact_form_redirects
                    .billing_form_url
                "
                :placeholder="
                  $t(
                    'forntend_checkout_link_advance_multistep_billing_form_url'
                  )
                "
                :validator="
                  $v.localModel.multi_step_redirect_options
                    .contact_form_redirects.billing_form_url
                "
              />
            </div>
          </div>
          <div
            class="gs-checkout-flow-options__field"
            v-if="
              localModel.checkout_link_page_options.move_password_to_last_step
            "
          >
            <label>{{ $t('forntend_checkout_link_advance_multistep_password_form_url') }}</label>
            <div>
              <text-input
                class="w-full"
                v-model="
                  localModel.multi_step_redirect_options.contact_form_redirects
                    .password_form_url
                "
                :placeholder="
                  $t(
                    'forntend_checkout_link_advance_multistep_password_form_url'
                  )
                "
                :validator="
                  $v.localModel.multi_step_redirect_options
                    .contact_form_redirects.password_form_url
                "
              />
            </div>
          </div>
          <div class="gs-checkout-flow-options__field">
            <label>{{ $t('forntend_checkout_link_advance_multistep_payment_form_url') }}</label>
            <div>
              <text-input
                class="w-full"
                v-model="
                  localModel.multi_step_redirect_options.contact_form_redirects
                    .payment_form_url
                "
                :placeholder="
                  $t(
                    'forntend_checkout_link_advance_multistep_payment_form_url'
                  )
                "
                :validator="
                  $v.localModel.multi_step_redirect_options
                    .contact_form_redirects.payment_form_url
                "
              />
            </div>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import { requiredIf, url } from 'vuelidate/lib/validators';
import CheckoutRadioButton from './CheckoutRadioButton';

export default {
  name: 'CheckoutStepTypes',

  components: {
    CheckoutRadioButton,
  },

  props: {
    checkoutStepTypes: {
      required: true,
    },
    selectedCheckoutStepType: {
      required: true,
    },
    multiStepTypes: {
      required: true,
    },
    singleStepTypes: {
      required: true,
    },
    selectedMultiStepTypeProp: {
      required: true,
    },
    isMultiStepRedirectProp: {
      required: true,
    },
    model: {
      required: true,
    },
  },
  validations: {
    localModel: {
      multi_step_redirect_options: {
        order_form_redirects: {
          order_form_url: {
            required: requiredIf(function requireOrderFormUrl() {
              return this.selCheckoutStepType === 0
                && this.selectedMultiStepType === 3;
            }),
            url,
          },
        },
        email_form_redirects: {
          billing_form_url: {
            required: requiredIf(function requireBillingFormUrl() {
              return this.selCheckoutStepType === 0
                && this.selectedMultiStepType === 2;
            }),
            url,
          },
          payment_form_url: {
            required: requiredIf(function requirePaymentFormUrl() {
              return this.selCheckoutStepType === 0
                && this.selectedMultiStepType === 2;
            }),
            url,
          },
          password_form_url: {
            required: requiredIf(function requirePasswordFormUrl() {
              return this.selCheckoutStepType === 0
                && this.selectedMultiStepType === 2
                && this.localModel.checkout_link_page_options
                  .move_password_to_last_step;
            }),
            url,
          },
        },
        contact_form_redirects: {
          billing_form_url: {
            required: requiredIf(function requireContactBillingFormUrl() {
              return this.selCheckoutStepType === 0
                && this.selectedMultiStepType === 1;
            }),
            url,
          },
          payment_form_url: {
            required: requiredIf(function requireContactPaymentFormUrl() {
              return this.selCheckoutStepType === 0
                && this.selectedMultiStepType === 1;
            }),
            url,
          },
          password_form_url: {
            required: requiredIf(function requireContactPasswordFormUrl() {
              return this.selCheckoutStepType === 0
                && this.selectedMultiStepType === 1
                && this.localModel.checkout_link_page_options
                  .move_password_to_last_step;
            }),
            url,
          },
        },
      },
    },
  },

  data() {
    return {
      localModel: this.model,
      selectedMultiStepType: this.selectedMultiStepTypeProp,
      isMultiStepRedirect: this.isMultiStepRedirectProp,
      selCheckoutStepType: this.selectedCheckoutStepType,
      selectedSingleStepType: 1,
    };
  },
  watch: {
    localModel() {
      this.$emit('updateModel', this.localModel);
    },
    'localModel.multi_step_redirect_options': {
      handler(after, before) {
        this.$emit(
          'updateMultiStepRedirect',
          this.localModel.multi_step_redirect_options
        );
      },
      deep: true,
    },
  },

  computed: {},

  methods: {
    changeStep(value) {
      this.$emit('updateSelCheckoutStepType', value);
    },

    multiStepTypeChange(value) {
      this.$emit('updateselectedMultiStepType', value);
    },

    singleStepTypeChange(value) {
      this.$emit('updateselectedSingleStepType', value);
    },

    multiStepRedirectChange() {
      this.$emit('updateIsMultiStepRedirect', this.isMultiStepRedirect);
    },
  },
};
</script>

<style scoped>
.gs-checkout-flow-options {
  display: grid;
  gap: 18px;
}

.gs-checkout-flow-options__section {
  padding: 20px;
  background: #fff;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 12px;
}

.gs-checkout-flow-options__copy {
  margin-bottom: 16px;
}

.gs-checkout-flow-options h3,
.gs-checkout-flow-options p {
  margin: 0;
}

.gs-checkout-flow-options h3,
.gs-checkout-flow-options__field > label {
  color: var(--gs-foreground, #182230);
  font-size: 14px;
  font-weight: 800;
}

.gs-checkout-flow-options p {
  margin-top: 4px;
  color: var(--gs-muted-foreground, #667085);
  font-size: 12px;
  line-height: 1.5;
}

.gs-checkout-flow-options__type-grid {
  display: grid;
  max-width: 720px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.gs-checkout-flow-options__layout-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.gs-checkout-flow-options__layout-grid--single {
  max-width: 360px;
  grid-template-columns: 1fr;
}

.gs-checkout-flow-options__switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.gs-checkout-flow-options__redirects {
  display: grid;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid var(--gs-border, #e2e8f0);
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.gs-checkout-flow-options__field > label {
  display: block;
  margin-bottom: 7px;
  font-size: 12px;
}
</style>
